import React, { memo, useEffect, useMemo, useState } from 'react'
import Facade from '@whitecobalt/tungsten/esm/components/Facade';
import FileKeyLoader from '@whitecobalt/tungsten/esm/components/FileKeyLoader';
import Icon from '@whitecobalt/tungsten/esm/components/Icon';
import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { useScreenTitle } from '@whitecobalt/tungsten/hooks/useScreenTitle'
import { useEffectMounted } from '@whitecobalt/tungsten/hooks/useEffectMounted'
import { ProductLinePropType, ProductLinesSearchQuery, ShopProductDoorCoresQuery, ShopProductDoorCoresQueryType } from '@services/query/EcommerceQueries';
import ProductListContainer from '../Prefabs/ProductListContainer';
import ProductFilter, { StateProps } from '../Prefabs/ProductFilter';
import ProductThumbnail from '../Prefabs/ProductThumbnail';
import { useAuthAPI } from '@whitecobalt/tungsten/esm/common/hooks/useAuthAPI';
import { emptyArray } from '@whitecobalt/tungsten/esm/common/utils/assets';

interface DoorCoreListProps {
	type: '3-layer-particle-boards' | 'solid-timber-cores' | 'mineral-cores';
	menuDescription?: string;
	shopLinks: {
        label: string;
        value: string;
    }[],
	productTypeURL?: string;
	productCategoryURL?: string;
}

const DoorCoreList: React.FunctionComponent<DoorCoreListProps> = memo(({type, menuDescription, shopLinks, productTypeURL, productCategoryURL}) => {
	const is3Layer = type === '3-layer-particle-boards'
    const isSolidTimber = type === 'solid-timber-cores'
    const isMineralCore = type === 'mineral-cores'

    const title = is3Layer ? '3-Layer Particle Board' : isSolidTimber ? 'Solid Timber Core' : 'Mineral Core'

    useScreenTitle(`Halspan Store - ${title}`);
	
	const history = useHistory()

	const [filters, setFilters] = useState<Pick<StateProps, 'fireRating'>>({
		fireRating: [],
	})

	const [loadProducts, requestProducts] = useLazyQuery<ShopProductDoorCoresQueryType>(ShopProductDoorCoresQuery, {
		fetchPolicy: 'no-cache'
	})
	
	const variables = {
		request: {
			ProductTypeID: 1,
			MaterialTypeID: is3Layer ? 1 : isSolidTimber ? 2 : 3,
			FireRatingIDs: filters.fireRating.length > 0 ? filters.fireRating : null
		}
	}

	useEffect(() => {
		loadProducts({
			variables
		})
	}, [type])

	useEffectMounted(() => {
		const cleanup = setTimeout(() => {
			loadProducts({
				variables
			})
		}, 1000)
		
		return () => clearTimeout(cleanup)
	}, [filters])

	const data = requestProducts.data?.ShopProductDoorCores.items || emptyArray

	const fileKeys = useMemo(() => {
		return data.filter((each) => !!each.ProductLineImages[0]?.ImageUrl).map((each) => {
			return each.ProductLineImages[0]?.ImageUrl
		})
	}, [data])

	const multiFetchPayload = useMemo(() => ({
		"maxWidthInPX": 175,
		"maxHeightInPX": 175,
	}), [])

    return (
		<>
			<div className="row">
				<div className="col-md-3 wc-sidebar-menu wc-products-home-side-menu wc-search-products">
					<ProductFilter 
						productTypeURL={productTypeURL}
						productCategoryURL={productCategoryURL}
						shopLinks={shopLinks}
						productTypeName="Door Blanks & Cores"
						productCategory={title}
						menuDescription={menuDescription}
						hasFireRating
						onChange={(events) => {
							setFilters(prev => ({
								...prev,
								...events
							}))
						}} />
				</div>
				<div className="col-md-9">
					<div className="row">
						<FileKeyLoader.MultiFetch
							fileKeys={fileKeys}
							payload={multiFetchPayload}
							cache
							cacheUniqueString="175px"
						>
							{data.length > 0 ? data.map((item) => {
								return (
									<div key={item.ID} className="col-sm-6 col-lg-4 col-xl-3">
										<ProductThumbnail 
											productName={item.Name} 
											fileKey={item.ProductLineImages[0]?.ImageUrl}
											url={`/store/door-core/view/${item.ID}`}/>
									</div>
								)
							}) : requestProducts.loading ? (
								new Array(8).fill(0).map((_: any, index) => (
									<div key={index} className="col-sm-6 col-lg-4 col-xl-3 wc-product-search-item">
										<div className="product-thumbnail">
											<h4><Facade title rows={0} padding={false} rowListStyle={{margin: 0}} /></h4>
											<Facade rows={1} padding={false} rowListStyle={{margin: 0}} rowStyle={{ borderRadius: 0, width: '100%', height: 150}} />
										</div>
									</div>
								))
							) : (
								<div className="d-flex justify-content-center w-100">
									<div className="text-center my-5">
										<Icon name="inbox" size={25}/>
										<h4 className="mt-2">No {title} to display</h4>
									</div>
								</div>
							)}
						</FileKeyLoader.MultiFetch>
					</div>
				</div>
			</div>                
		</>
    )
})

export default DoorCoreList